import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKeyhole, Eye, EyeOff } from "lucide-react";

// Iter 136: pagina raggiunta dal link email (?token=...) per scegliere la nuova password
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("La password deve avere almeno 6 caratteri");
    if (pwd !== pwd2) return setError("Le due password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password aggiornata: ora puoi accedere con quella nuova");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8" data-testid="reset-password-page">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-blue-50 text-[#0044FF] flex items-center justify-center">
            <LockKeyhole className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold leading-tight">Scegli la nuova password</h1>
            <p className="text-sm text-slate-500">Almeno 6 caratteri. Poi accedi normalmente.</p>
          </div>
        </div>
        {!token ? (
          <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700" data-testid="reset-missing-token">
            Link incompleto: apri di nuovo il link ricevuto via email oppure{" "}
            <Link to="/password-dimenticata" className="underline font-bold">richiedi un nuovo recupero</Link>.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="rp-pwd" className="text-sm font-bold">Nuova password</Label>
              <div className="relative">
                <Input
                  id="rp-pwd"
                  type={show ? "text" : "password"}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  autoComplete="new-password"
                  className="h-14 text-base pr-12"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="reset-toggle-visibility"
                  aria-label={show ? "Nascondi password" : "Mostra password"}
                >
                  {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-pwd2" className="text-sm font-bold">Ripeti la nuova password</Label>
              <Input
                id="rp-pwd2"
                type={show ? "text" : "password"}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                autoComplete="new-password"
                className="h-14 text-base"
                data-testid="reset-password-confirm-input"
              />
            </div>
            {error && (
              <div role="alert" data-testid="reset-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}{" "}
                {/scaduto|non valido/i.test(error) && (
                  <Link to="/password-dimenticata" className="underline font-bold" data-testid="reset-request-again-link">Richiedi un nuovo link</Link>
                )}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full h-14 text-base font-bold bg-[#0044FF] hover:bg-[#0033CC]">
              {loading ? "Salvataggio…" : "Salva la nuova password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
